const LABEL_GAP = 10
const DESKTOP_MQ = '(min-width: 768px)'

/**
 * Push overlapping map labels apart vertically.
 *
 * Labels are grouped per map (via [data-day-map]) and then per side
 * (via [data-side]), since a left-pointing and right-pointing label never
 * actually collide. Within each group we sort top-to-bottom and shift any
 * label that would overlap the previous one. A manual base offset can be set
 * per label with data-offset-y and it will be preserved.
 *
 * Safe to call repeatedly: it re-establishes each label's base position
 * before measuring, so it's idempotent and correct after resizes.
 *
 * NOTE: [data-day-map] must be the per-view marker layer, not a shared map
 * container. If several itineraries/days stack their pins over one visual map,
 * put data-day-map on each layer so their labels are grouped separately.
 */
export function declutterLabels(scope = document, gap = LABEL_GAP) {
	const maps = [...scope.querySelectorAll('[data-day-map]')]
	if (!maps.length) return

	const isDesktop = window.matchMedia(DESKTOP_MQ).matches

	maps.forEach(map => {
		const labels = [...map.querySelectorAll('[data-map-label]')]
		if (!labels.length) return

		// Reset each label to its base position (respecting any manual nudge).
		// This is also all that's needed on mobile, where labels are hidden.
		const bases = new Map()
		labels.forEach(label => {
			const base = parseFloat(label.dataset.offsetY || '0') || 0
			bases.set(label, base)
			label.style.transform = `translateY(calc(-50% + ${base}px))`
		})

		if (!isDesktop) return

		// A layer hidden with display:none has no geometry to measure; skip it
		// now and let a later re-run (e.g. on view switch) handle it.
		const mapRect = map.getBoundingClientRect()
		if (!mapRect.height) return

		const groups = { left: [], right: [] }
		labels.forEach(label => {
			const side = label.dataset.side === 'left' ? 'left' : 'right'
			const rect = label.getBoundingClientRect()
			groups[side].push({ el: label, top: rect.top - mapRect.top, height: rect.height })
		})

		Object.values(groups).forEach(group => {
			group.sort((a, b) => a.top - b.top)
			let prevBottom = -Infinity
			group.forEach(box => {
				let shift = 0
				if (box.top < prevBottom + gap) shift = prevBottom + gap - box.top
				const base = bases.get(box.el)
				box.el.style.transform = `translateY(calc(-50% + ${base + shift}px))`
				prevBottom = box.top + shift + box.height
			})
		})
	})
}

let started = false

/**
 * Wire up automatic decluttering: on DOM ready, once web fonts load (label
 * metrics can shift), on window load, and debounced on resize. Guarded so it
 * only attaches listeners once even if imported from multiple entry points.
 */
export function initMapLabels() {
	if (started) return
	started = true

	const run = () => declutterLabels(document)

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', run, { once: true })
	} else {
		run()
	}

	if (document.fonts && document.fonts.ready) document.fonts.ready.then(run)
	window.addEventListener('load', run)

	let resizeTimer
	window.addEventListener('resize', () => {
		clearTimeout(resizeTimer)
		resizeTimer = setTimeout(run, 150)
	})
}

// Auto-initialise on import.
initMapLabels()